import { HttpError } from '@/errors/HttpError';

export const NETWORK_UNAVAILABLE_MESSAGE =
  'Unable to reach the server. Check your connection and try again.';

/** Rate limits / upstream capacity — never expose provider or endpoint details. */
export const SERVICE_BUSY_MESSAGE =
  'The service is temporarily busy. Please try again in a few minutes.';

// ⚠️ Este tope descarta el mensaje del backend y muestra el genérico. Un 4xx cuyo texto
// ES la instrucción —el 422 del parser de la Ad Library dice qué URL pegar— tiene que
// caber aquí, y el core lo comprueba con un test propio. Subirlo o bajarlo obliga a
// revisar `_UNPARSEABLE_URL_DETAIL` en measure-ease-app-core.
const MAX_USER_FACING_ERROR_LENGTH = 180;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const firstPydanticMsg = (detail: unknown): string | null => {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const first = detail[0];
  if (!isRecord(first) || typeof first.msg !== 'string') return null;
  return first.msg;
};

const stringFromDetailField = (data: unknown): string | null => {
  if (!isRecord(data) || !('detail' in data)) return null;
  const detail = data.detail;
  if (typeof detail === 'string') return detail;
  return firstPydanticMsg(detail);
};

const stringFromErrorEnvelope = (data: unknown): string | null => {
  if (!isRecord(data) || !('error' in data)) return null;
  const err = data.error;
  if (!isRecord(err) || typeof err.message !== 'string') return null;
  return err.message;
};

const looksLikeInternalDump = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    message.length > MAX_USER_FACING_ERROR_LENGTH ||
    lower.includes('traceback') ||
    lower.includes('connection_lost') ||
    lower.includes('starlette') ||
    lower.includes('sqlalchemy') ||
    lower.includes('asyncpg') ||
    message.includes('<!DOCTYPE') ||
    message.includes('<html')
  );
};

/**
 * Backend/worker errors that name vendors (RapidAPI), paths (/community), retries, or
 * raw HTTP codes. Showing them leaks how the platform is wired.
 */
export const looksLikeUpstreamProviderLeak = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('rapidapi') ||
    lower.includes('rapid api') ||
    /\bagot[oó]\s+\d+\s+intentos?\b/i.test(message) ||
    /\bdevolvi[oó]\s+\d{3}\b/i.test(message) ||
    /\/(?:community|audience|posts|users|followers|media|report|dict|history|insights)\b/i.test(
      message
    ) ||
    /\b(?:upstream|provider)\b.{0,40}\b(?:error|failed|fail|429|500|502|503)\b/i.test(message)
  );
};

export const looksLikeRateLimitSignal = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    /\b429\b/.test(message) ||
    lower.includes('too many requests') ||
    lower.includes('rate limit') ||
    lower.includes('rate_limit') ||
    lower.includes('rate-limit')
  );
};

const looksLikeConnectivityFailure = (message: string, status?: number): boolean => {
  if (status === 0 || status === 502 || status === 503 || status === 504) return true;
  const lower = message.toLowerCase();
  return (
    lower.includes('network error') ||
    lower.includes('unreachable') ||
    lower.includes('failed to fetch') ||
    lower.includes('econnrefused') ||
    lower.includes('connection refused') ||
    lower.includes('service temporarily unavailable') ||
    lower.includes('connection_lost')
  );
};

/** Collapse API/network dumps into a short message safe to show in the UI. */
export const sanitizeUserFacingError = (
  message: string | null | undefined,
  fallback: string
): string => {
  if (!message?.trim()) return fallback;
  const trimmed = message.trim();
  if (looksLikeConnectivityFailure(trimmed)) {
    return NETWORK_UNAVAILABLE_MESSAGE;
  }
  if (looksLikeUpstreamProviderLeak(trimmed)) {
    return looksLikeRateLimitSignal(trimmed) ? SERVICE_BUSY_MESSAGE : fallback;
  }
  if (looksLikeInternalDump(trimmed)) {
    return NETWORK_UNAVAILABLE_MESSAGE;
  }
  if (trimmed.length > MAX_USER_FACING_ERROR_LENGTH) {
    return fallback;
  }
  return trimmed;
};

/** First detail string from an HTTP error (Pydantic array → first `msg`). */
export const getHttpErrorDetailString = (error: unknown): string | null => {
  if (!(error instanceof HttpError)) return null;
  return stringFromDetailField(error.response.data);
};

/** Resolves a user-facing message from API error payloads (BFF / FastAPI). */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof HttpError)) {
    return fallback;
  }

  if (looksLikeConnectivityFailure(error.message, error.response.status)) {
    return NETWORK_UNAVAILABLE_MESSAGE;
  }

  const data = error.response.data;
  if (typeof data === 'string') {
    return sanitizeUserFacingError(data, fallback);
  }

  const fromDetail = stringFromDetailField(data);
  if (fromDetail) return sanitizeUserFacingError(fromDetail, fallback);

  const fromEnvelope = stringFromErrorEnvelope(data);
  if (fromEnvelope) return sanitizeUserFacingError(fromEnvelope, fallback);

  if (error.message) return sanitizeUserFacingError(error.message, fallback);
  return fallback;
};
